import {
  Box,
  Button,
  HStack,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {EtoilesIcon} from '@/components/svg/EtoilesIcon';
import {useTranslation} from 'react-i18next';
import {useCallback, useRef, useState} from 'react';
import {useCommand, useHalState} from '@synako/haligator';
import {
  useAnnulationSuggestions,
  useLivraisonsPossiblesContext,
} from 'ff-common';
import {useNavigate} from 'react-router-dom';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {PanierLink} from '@/domains/panier/controllers/PanierLink';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {ArticlesCadencierSuggestionsAjoutesEventDesc} from 'api-types/ffconnect-hal';
import {State} from '@synako/halitrek';
import {useClient} from '@/haligator-factory';

interface SuggestionActionsProps {
  onSuggestionSuccess?: (
    event: State<ArticlesCadencierSuggestionsAjoutesEventDesc> | null,
  ) => void;
}
// Bouton pour tout ajouter d'un coup tous les articles ayant une suggestion de quantité
export const SuggestionActions: React.FC<SuggestionActionsProps> = ({
  onSuggestionSuccess,
}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const toast = useToast();
  const navigate = useNavigate();
  const client = useClient();
  const {state: suggestionsState} = useHalState(client.follow('suggestions'));
  const {livraisonSelectionnee} = useLivraisonsPossiblesContext();
  const {annulerSuggestions} = useAnnulationSuggestions();
  // Stocke l'événement d'ajout pour pouvoir annuler
  const lastAddEventRef =
    useRef<State<ArticlesCadencierSuggestionsAjoutesEventDesc> | null>(null);
  const [isAnnulationEnCours, setIsAnnulationEnCours] = useState(false);
  // Stocke l'ID du toast pour pouvoir le fermer
  const toastIdRef = useRef<string | undefined>('ajoute-commande-panier-toast');

  const hasSuggestions = !!suggestionsState?.data.suggestions.length;

  const onSuccess = useCallback(
    (eventState: State<ArticlesCadencierSuggestionsAjoutesEventDesc>) => {
      // Récupération du nombre d'articles depuis l'événement
      const articles = eventState.data.data.panier.nombre_articles_ajoutes ?? 0;
      // Stocke l'évent pour l'annulation
      lastAddEventRef.current = eventState;

      // Fonction pour annuler les articles ajoutés
      const handleAnnuler = async () => {
        const event = lastAddEventRef.current;
        if (!event) return;
        // Fermeture du toast
        const closeToast = () => {
          if (toastIdRef.current) {
            toast.close(toastIdRef.current);
          }
        };
        // hook pour annuler l'ajout au panier des suggestions de quantité
        await annulerSuggestions(
          setIsAnnulationEnCours,
          textToast,
          closeToast,
          event,
        );

        // Reset l'événement après annulation
        lastAddEventRef.current = null;

        // Si fonction callback, appele avec null pour signaler l'annulation
        if (onSuggestionSuccess) {
          onSuggestionSuccess(null);
        }
      };

      // recuperation de l'event pour le remonter et l'utiliser pour l'affichage du picto
      if (onSuggestionSuccess) {
        onSuggestionSuccess(eventState);
      }

      // Afficher le toast avec les articles ajoutés
      textToast(
        [
          t('cadencier.suggestion.articles_ajoutes_au_panier', {
            count: articles,
          }),
          <HStack
            key='action-buttons'
            spacing={2}
          >
            <PanierLink
              t={t}
              date_de_livraison={
                livraisonSelectionnee.data.tolede.date_de_livraison
              }
              navigate={navigate}
            />
            <Text>•</Text>
            <Button
              key='annuler-cta'
              onClick={handleAnnuler}
              variant='unstyled'
              fontWeight='bold'
              textDecoration='underline'
              disabled={isAnnulationEnCours}
              isLoading={isAnnulationEnCours}
            >
              {t('global.annuler')}
            </Button>
          </HStack>,
        ],
        {id: toastIdRef.current, duration: 5000},
      ); // Utilisation du même ID stockée dans la ref + delai pour laisser le temps d'annuler
    },
    [
      livraisonSelectionnee,
      navigate,
      t,
      textToast,
      toast,
      onSuggestionSuccess,
      isAnnulationEnCours,
      annulerSuggestions,
    ],
  );

  const onError = useDefaultOnError(textToast);

  const {submit: submitAjouterArticlesSuggeres, isSubmitting} = useCommand(
    livraisonSelectionnee,
    'c:ajouter-suggestions-articles-cadencier-au-panier',
    {onSuccess, onError},
  );

  const handleClick = useCallback(() => {
    submitAjouterArticlesSuggeres({});
  }, [submitAjouterArticlesSuggeres]);

  return (
    <Box w={{base: '100%', md: 'auto'}}>
      <Tooltip
        bg='white'
        color='blue.main'
        border='1px solid'
        borderColor='blue.border'
        borderRadius='8px'
        boxShadow='md'
        p='3'
        label={
          !hasSuggestions ? (
            <Text
              fontSize='sm'
              color='blue.main'
            >
              {t('cadencier.suggestion.pas_suggestions_disponibles')}
            </Text>
          ) : (
            <VStack
              alignItems='flex-start'
              spacing='1'
            >
              <Text
                fontSize='sm'
                fontWeight='700'
                color='blue.main'
              >
                {t('cadencier.suggestion.remplissez_votre_panier')}
              </Text>
              <Text
                fontSize='sm'
                color='blue.main'
              >
                {t('cadencier.suggestion.ajoutez_en_un_clic')}
              </Text>
            </VStack>
          )
        }
      >
        <Button
          leftIcon={
            isSubmitting ? (
              <Spinner
                size='sm'
                color='white'
              />
            ) : (
              <EtoilesIcon color='white' />
            )
          }
          bgGradient='linear(to-r, blue.main, blue.sky)'
          color='white'
          fontSize='sm'
          height='38px'
          borderRadius='8px'
          px='10px'
          w='100%'
          onClick={handleClick}
          isDisabled={isSubmitting || !hasSuggestions}
          _hover={!hasSuggestions ? {opacity: 0.4} : {opacity: 0.8}}
          whiteSpace='nowrap'
          flexShrink={0}
        >
          {t('cadencier.suggestion.remplir_avec_suggestions')}
        </Button>
      </Tooltip>
    </Box>
  );
};
